import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../../../constants/common'
import { HTML_LINK_TARGET } from '../../../../constants/html'
import { ROUTE_PATH_LIST, SUBMENU_PARAM_KEY } from '../../../../constants/route'
import {
  useLinkLikeClick,
  useMinimizedModalsContext,
  useNotification,
} from '../../../../hooks'
import { ConfirmationModal } from '../../../Confirmation'
import { CONFIRMATION_QA_NAME_FORM_MODAL } from '../../../Form'
import { MINIMIZED_MODAL_SCOPE } from '../../../Modal'
import {
  EMAIL_PROFILE_ACTION_ADD_NOTE,
  EMAIL_PROFILE_ACTION_ARCHIVE,
  EMAIL_PROFILE_ACTION_DOWNLOAD_FILES,
  EMAIL_PROFILE_ACTION_FORWARD,
  EMAIL_PROFILE_ACTION_MARK_AS_IMPORTANT,
  EMAIL_PROFILE_ACTION_MARK_AS_UNIMPORTANT,
  EMAIL_PROFILE_ACTION_MARK_AS_UNREAD,
  EMAIL_PROFILE_ACTION_PRINT,
  EMAIL_PROFILE_ACTION_REPLY,
  EMAIL_PROFILE_ACTION_REPLY_ALL,
  EMAIL_PROFILE_ACTION_RESTORE,
} from '../../Email.constants'
import {
  EmailAddNoteActionModal,
  getEmailAddNoteInitialValues,
} from '../../EmailAddNoteActionModal'
import { useEmailFilesDownloadAll } from '../useEmailFilesDownloadAll'
import { useEmailProfileContext } from '../useEmailProfileContext'

import {
  EMAIL_PROFILE_ACTION_ERROR_TITLE,
  EMAIL_PROFILE_ACTION_SUCCESS_TITLE,
} from './useEmailProfileActions.constants'

/**
 * Hook that provides action handlers for email profile operations
 * such as reply, forward, archive, restore, mark as unread, and update.
 *
 * @param {object} [params]
 * @param {Record<string, string>} [params.errorTitle=EMAIL_PROFILE_ACTION_ERROR_TITLE]
 *    Error notification titles mapped by email profile action.
 * @param {({ id: string }) => string} [params.getPrintPageLink]
 *    Returns print page link for the provided email id.
 * @param {string} [params.minimizedModalScope=MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE]
 *    Scope of the subtree hosting this email profile; state-based actions that
 *    close it prompt only for dirty modals with the same scope.
 * @param {boolean} [params.shouldNavigateToList=true]
 *    Whether to navigate to mail list after actions such as archive, restore,
 *    or mark as unread when applicable.
 * @param {Record<string, string>} [params.successTitle=EMAIL_PROFILE_ACTION_SUCCESS_TITLE]
 *    Success notification titles mapped by email profile action.
 * @param {() => void} [params.onForward]
 *    Called when forward action is triggered.
 * @param {() => void} [params.onReply]
 *    Called when reply action is triggered.
 * @param {() => void} [params.onReplyAll]
 *    Called when reply all action is triggered.
 * @param {() => void} [params.onSuccessArchive]
 *    Called after an archive removes the whole thread from the list (the last
 *    message was archived) and navigation to the mail list is disabled.
 *    Falls back to `onSuccessUpdate` when not provided.
 * @param {({ id?: string, unread: boolean }) => void} [params.onSuccessMarkAsUnread]
 *    Called after successful mark as unread action.
 * @param {({ id?: string, messagesLength?: number }) => void} [params.onSuccessUpdate]
 *    Called after successful update entity.
 * @param {({ unread: boolean, uuid?: string }) => Promise<unknown>} [params.onUnread]
 *    Updates unread state for the provided email id.
 * @param {(data: object, successMessage?: string) => Promise<unknown>} [params.onUpdate]
 *    Custom update handler. Falls back to context `handleUpdateEntity`.
 *
 * @returns {object} Email profile action handlers.
 */
export function useEmailProfileActions({
  errorTitle = EMAIL_PROFILE_ACTION_ERROR_TITLE,
  getPrintPageLink,
  minimizedModalScope = MINIMIZED_MODAL_SCOPE.EMAIL_PROFILE,
  shouldNavigateToList = true,
  successTitle = EMAIL_PROFILE_ACTION_SUCCESS_TITLE,
  onForward,
  onReply,
  onReplyAll,
  onSuccessArchive,
  onSuccessMarkAsUnread,
  onSuccessUpdate,
  onUnread,
  onUpdate,
} = EMPTY_OBJECT) {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const {
    entityData,
    handleCloseModal,
    handleReloadEntity,
    handleUpdateEntity,
    mailUuid,
    messages,
    setModal,
    setPerformingAction,
    sub,
    widgetsData,
  } = useEmailProfileContext()

  const { handleAddErrorNotification, handleAddSuccessNotification } =
    useNotification()

  const { confirmBeforeAction } = useMinimizedModalsContext()

  const { handleClickDownloadAll } = useEmailFilesDownloadAll({
    files: widgetsData?.files.items || entityData?.files,
    onSetModal: setModal,
  })

  const { handleClick } = useLinkLikeClick()

  async function handleUpdate({ action, data, id, shouldReloadEntity = true }) {
    setPerformingAction(true)

    const updateHandler = onUpdate || handleUpdateEntity

    return updateHandler(
      { ...data, ...(id ? { id } : null) },
      successTitle[action]
    )
      .then(() => {
        if (shouldReloadEntity) {
          handleReloadEntity?.()

          onSuccessUpdate?.()
        }
      })
      .catch(() => {
        handleAddErrorNotification(errorTitle[action])

        throw Error
      })
      .finally(() => {
        handleCloseModal()
        setPerformingAction(false)
      })
  }

  function navigateToList() {
    navigate(`/${ROUTE_PATH_LIST.mails}`, { replace: true })
  }

  async function handleMarkAsImportant({ action, id, important }) {
    return handleUpdate({
      action,
      data: { starred: important },
      id,
    })
  }

  function showConfirmModal({ submitText, title, onSubmit }) {
    setModal(
      <ConfirmationModal
        actionButtonsProps={{
          submitText: t(submitText),
        }}
        description={t(title)}
        qaName={CONFIRMATION_QA_NAME_FORM_MODAL}
        onCancel={handleCloseModal}
        onSubmit={onSubmit}
      />
    )
  }

  function handleSubmitAddNote(id, values) {
    // api does not accept empty string for this field
    handleUpdate({
      action: EMAIL_PROFILE_ACTION_ADD_NOTE,
      data: { notes: values.notes || null },
    })
  }

  function handleOpenAddNoteModal() {
    setModal(
      <EmailAddNoteActionModal
        id={mailUuid}
        initialValues={getEmailAddNoteInitialValues(entityData?.notes)}
        onClose={handleCloseModal}
        onSubmit={handleSubmitAddNote}
      />
    )
  }

  // Archive/restore move the thread out of the current sub_menu, so the
  // entity refetch inside handleUpdate would respond 404; the handlers below
  // reload or navigate themselves instead.
  async function handleUpdateWithoutReload(params) {
    return handleUpdate({ ...params, shouldReloadEntity: false })
  }

  async function handleArchive({
    action,
    id,
    onArchive = handleUpdateWithoutReload,
  }) {
    return onArchive({
      action,
      data: { soft_archived: true },
      id,
    })
      .then(() => {
        handleAddSuccessNotification(successTitle[action])

        if (id && messages?.length > 1) {
          handleReloadEntity()
        } else if (shouldNavigateToList) {
          navigateToList()
        } else if (onSuccessArchive) {
          onSuccessArchive()
        } else {
          onSuccessUpdate?.()
        }
      })
      .catch(() => {
        handleAddErrorNotification(errorTitle[action])
      })
      .finally(() => {
        handleCloseModal()
        setPerformingAction(false)
      })
  }

  async function handleRestore({ action, id }) {
    return handleUpdateWithoutReload({
      action,
      data: { soft_archived: false },
      id,
    }).then(() => {
      if (id && messages?.length > 1) {
        handleReloadEntity()

        onSuccessUpdate?.()
      } else if (shouldNavigateToList) {
        navigateToList()
      } else {
        onSuccessUpdate?.()
      }
    })
  }

  function getHandleArchive({ action, id, onArchive }) {
    return () => handleArchive({ action, id, onArchive })
  }

  function getHandleRestore({ action, id }) {
    return () => handleRestore({ action, id })
  }

  function handleMarkAsUnread({ action, id, unread }) {
    function performAction() {
      setPerformingAction(true)

      onUnread({
        unread,
        uuid: id,
      })
        .then(() => {
          handleAddSuccessNotification(successTitle[action])

          if (shouldNavigateToList) {
            navigateToList()
          }

          onSuccessMarkAsUnread?.({ id, unread })
        })
        .catch(() => {
          handleAddErrorNotification(errorTitle[action])
        })
        .finally(() => {
          setPerformingAction(false)
        })
    }

    // When the action only closes the preview (state-based) we gate thecall,
    //  before the API runs so cancelling the dirty tray modal prompt truly stops the action.
    if (!shouldNavigateToList && confirmBeforeAction) {
      confirmBeforeAction(performAction, {
        scope: minimizedModalScope,
      })

      return
    }

    performAction()
  }

  function handleClickPrint({ event, id = mailUuid }) {
    handleClick({
      event,
      link: getPrintPageLink({
        additionalParams: sub ? `?${SUBMENU_PARAM_KEY}=${sub}` : EMPTY_STRING,
        id,
      }),
      target: HTML_LINK_TARGET.blank,
    })
  }

  function handleClickAction({ event, id, value, onArchive }) {
    switch (value) {
      case EMAIL_PROFILE_ACTION_MARK_AS_IMPORTANT:
        handleMarkAsImportant({ action: value, id, important: true })
        break
      case EMAIL_PROFILE_ACTION_MARK_AS_UNIMPORTANT:
        handleMarkAsImportant({ action: value, id, important: false })
        break

      case EMAIL_PROFILE_ACTION_ADD_NOTE:
        handleOpenAddNoteModal()
        break

      case EMAIL_PROFILE_ACTION_ARCHIVE:
        showConfirmModal({
          submitText: 'Modal.EmailProfileArchiveBtnKey',
          title: 'Modal.EmailProfileArchiveConfirmationTextKey',
          onSubmit: getHandleArchive({ action: value, id, onArchive }),
        })
        break

      case EMAIL_PROFILE_ACTION_RESTORE:
        showConfirmModal({
          submitText: 'Modal.EmailProfileRestoreBtnKey',
          title: 'Modal.EmailProfileRestoreConfirmationTextKey',
          onSubmit: getHandleRestore({ action: value, id }),
        })
        break

      case EMAIL_PROFILE_ACTION_DOWNLOAD_FILES:
        handleClickDownloadAll()
        break

      case EMAIL_PROFILE_ACTION_MARK_AS_UNREAD:
        handleMarkAsUnread({ action: value, id, unread: true })
        break

      case EMAIL_PROFILE_ACTION_PRINT:
        handleClickPrint({ event, id })
        break

      case EMAIL_PROFILE_ACTION_REPLY:
        onReply()
        break

      case EMAIL_PROFILE_ACTION_REPLY_ALL:
        onReplyAll()
        break

      case EMAIL_PROFILE_ACTION_FORWARD:
        onForward()
        break

      default:
        break
    }
  }

  return {
    handleClickAction,
    handleUpdate,
  }
}
